'use client';

import type { ReactElement} from 'react';
import { useCallback, useMemo, useState } from 'react';

import type { Swiper as SwiperType } from 'swiper';
import { Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { Modal } from '@ui/uikit/components/common/Modal';
import type { Breakpoint} from '@ui/uikit/styles/exports/breakpoints';
import { mapBreakpoints } from '@ui/uikit/styles/exports/breakpoints';
import type { MediaAsset } from '@ui/uikit/types/MediaAsset';
import { OneOrMany } from '@utilities/helpers/OneOrMany';
import { Image } from '@utilities/Image';

import type { AdaptiveGalleryProps } from '../../AdaptiveGallery';
import { AdaptiveGallery } from '../../AdaptiveGallery';
import styles from './MediaGallery.module.scss';
import { PreviewSwiperPagination } from './PreviewSwiperPagination';

type MediaAssetSizeSelector = `${number}px` | `${number}vw`;

interface MediaGalleryBaseProps extends Omit<AdaptiveGalleryProps, 'breakpoints' | 'children'> {
  mediaSize?: MediaAssetSizeSelector;
  loading?: 'eager' | 'lazy';
}

type MediaGalleryBreakpointOverrides = Partial<MediaGalleryBaseProps>;

interface MediaGalleryProps extends MediaGalleryBaseProps {
  children?: OneOrMany<ReactElement<MediaAsset, typeof MediaGalleryAsset>>;
  breakpoints?: Partial<Record<Breakpoint, Partial<MediaGalleryBreakpointOverrides>>>;
}

const resolveBreakpointMediaSize = (overrides: MediaGalleryBreakpointOverrides) => {
  return overrides.mediaSize
    ?? (overrides.numberOfSlides ? `${ 100 / overrides.numberOfSlides }vw` : undefined);
};

const MediaGallery = ({
  children = [],
  slideAspectRatio,
  mediaSize: defaultMediaSize = '100vw',
  loading: defaultLoading,
  breakpoints: defaultBreakpoints,
  ...rest
}: MediaGalleryProps) => {
  const [assetPreviewUrl, setAssetPreviewUrl] = useState<string>();
  const [currentSlideIndex, setCurrentSlideIndex] = useState<number>(0);
  const [swiperInstance, setSwiperInstance] = useState<SwiperType | null>(null);

  const handleClosePreview = useCallback(() => {
    setAssetPreviewUrl(undefined);
    setCurrentSlideIndex(0);
  }, []);

  const slides = useMemo(() => OneOrMany.asArray(children), [children]);

  const mediaSizes = useMemo(() => {
    const queries = mapBreakpoints((breakpoint, viewportSize) => {
      const mediaSize = defaultBreakpoints?.[breakpoint]
        ? resolveBreakpointMediaSize(defaultBreakpoints[breakpoint])
        : undefined;

      return mediaSize ? `(max-size: ${ viewportSize }px) ${mediaSize}` : '';
    });

    queries.push(defaultMediaSize);

    return queries.join(', ');
  }, [defaultBreakpoints, defaultMediaSize]);

  // const breakpoints = useMemo(() => {
  //   return mapValues(defaultBreakpoints, (overrides = {}) => ({
  //     ...overrides,
  //     ...()
  //     slideWidth: overrides.slideWidth ?? (overrides.numberOfSlides ? (1 / overrides.numberOfSlides) : undefined),
  //   }));
  // }, [defaultBreakpoints]);

  return (
    <div
      className={styles['container']}
      style={{
        width: '100%',
        maxWidth: '1376px',
      }}
    >
      <AdaptiveGallery
        slideWidth={`${100 / 3}%`}
        slideAspectRatio={slideAspectRatio}
        spacing="16px"
        centerSlides={false}
        breakpoints={defaultBreakpoints}
        {...rest}
      >
        {slides.map(({ props: { url, description, loading = defaultLoading }, key }, index) => {
          return (
            <AdaptiveGallery.Slide key={key}>
              <Image
                src={url}
                alt={description || ''}
                fill={true}
                sizes={mediaSizes}
                className={styles['asset']}
                loading={loading}
                priority={loading === 'eager'}
                fetchPriority={ loading === 'eager' ? 'high' : 'auto' }
                onClick={() => {
                  setAssetPreviewUrl(url);
                  setCurrentSlideIndex(index);
                }}
              />
            </AdaptiveGallery.Slide>
          );
        })}
      </AdaptiveGallery>

      <Modal
        isOpen={!!assetPreviewUrl}
        onClose={handleClosePreview}
      >
        <div className={styles['preview']}>
          <Swiper
            slidesPerView={1}
            centeredSlides
            initialSlide={currentSlideIndex}
            loop={slides.length > 1}
            modules={[Pagination]}
            onSwiper={setSwiperInstance}
            className={styles['swiper']}
          >
            {slides.map(({ props: { url, description, loading }, key }) => (
              <SwiperSlide key={key} className={styles['slide']}>
                <div
                  className={styles['imageContainer']}
                  style={
                    {
                      aspectRatio: slideAspectRatio,
                      '--dynamic-max-width': `min(1376px, calc((100vh - 120px) * ${slideAspectRatio}))`,
                    } as React.CSSProperties
                  }
                >
                  <Image
                    src={url}
                    alt={description || ''}
                    fill
                    sizes={mediaSizes}
                    loading={loading}
                    className={styles['previewImage']}
                  />
                </div>
              </SwiperSlide>
            ))}
          </Swiper>

          <PreviewSwiperPagination swiper={swiperInstance} />
        </div>
      </Modal>
    </div>
  );
};

const MediaGalleryAsset = (props: MediaAsset) => {
  return false;
};

MediaGalleryAsset.displayName = 'MediaGallery.Asset';

MediaGallery.Asset = MediaGalleryAsset;

export { MediaGallery, MediaGalleryAsset };
